import { useState } from 'react';

import POSHeaderBar from '../pos/POSHeaderBar';

import POSTabSection from '../pos/POSTabSection';

import { useSale } from '../../../hooks/sale/useSale';

import POSQuickActions from '../pos/POSQuickActions';

import POSProductWorkspace from '../pos/products/POSProductWorkspace';

import POSSummaryPanel from '../pos/summary/POSSummaryPanel';

import MultiplePaymentModal from '../MultiplePayment/MultiplePaymentModal';

import POSUpdateSelectedProductModal from '../pos/modal/POSUpdateSelectedProductModal';

import { ProductPanelItem } from '../../../types/productpanel/ProductPanelItem';

import { useSaleDispatcher } from '../../../hooks/sale/useSaleDispatcher';
import POSAddProductToSaleModal from '../pos/modal/POSAddProductToSaleModal';
import useRecipeStockGuard from '../../../hooks/restaurant/useRecipeStockGuard';
import RecipeStockInfoModal from '../../../features/restaurant/pos/RecipeStockInfoModal';
import POSEmptyState from '../pos/empty/POSEmptyState';
import PendingSalesPanel from '../pos/tabs/PendingSales/PendingSalesPanel';
import PendingQuotationsPanel from '../pos/tabs/PendingQuotations/PendingQuotationsPanel';
import QuotationPriceCheckPanel from '../pos/tabs/PriceCheck/QuotationPriceCheckPanel';

const POSSaleRegistrationPanel = () => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const {
    form,

    activeBranchId,

    selectedProduct,

    handlerSelectProduct,

    showMultiplePaymentModal,

    closeMultiPaymentModal,
  } = useSale();

  const {
    addProduct,

    updateProduct,
  } = useSaleDispatcher();

  // Aviso informativo (no bloquea) al vender un producto con receta cuando la
  // sucursal tiene activo "Validar existencia antes de preparar".
  const recipeGuard = useRecipeStockGuard();

  // =====================================================
  // 🔹 Product Modal State
  // =====================================================
  const [selectedProductToAdd, setSelectedProductToAdd] =
    useState<ProductPanelItem | null>(null);

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handleOpenProductModal = (product: ProductPanelItem) => {
    setSelectedProductToAdd(product);
  };

  const handleCloseProductModal = () => {
    setSelectedProductToAdd(null);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <>
      <div className="flex flex-col gap-4">
        {/* ===================================================== */}
        {/* 🔹 POS Header */}
        {/* ===================================================== */}
        <POSHeaderBar />

        {/* ===================================================== */}
        {/* 🔹 Main Content */}
        {/* ===================================================== */}
        <div
          className="
            mb-5
            grid
            gap-4

            xl:grid-cols-[1fr_380px]
          "
        >
          {/* ===================================================== */}
          {/* 🔹 Workspace */}
          {/* ===================================================== */}
          <POSTabSection
            defaultTab="products"
            tabs={[
              {
                key: 'products',

                label: 'Productos',

                content: (
                  <POSProductWorkspace
                    onAddProductClick={handleOpenProductModal}
                  />
                ),
              },

              {
                key: 'quick-actions',

                label: 'Acciones rápidas',

                content: <POSQuickActions />,
              },

              {
                key: 'pending-sales',

                label: 'Ventas pendientes',

                content: <PendingSalesPanel />,
              },

              {
                key: 'pending-quotations',

                label: 'Cotizaciones pendientes',

                content: <PendingQuotationsPanel navigateOnContinue />,
              },

              {
                key: 'price-check',
                label: 'Consulta de Precios',
                content: <QuotationPriceCheckPanel />,
              },
            ]}
          />

          {/* ===================================================== */}
          {/* 🔹 Summary */}
          {/* ===================================================== */}
          <section
            className="
              sticky
              top-4
              h-fit
            "
          >
            {form?.id ? (
              <POSSummaryPanel />
            ) : (
              <POSEmptyState
                title="No hay venta activa"
                description="Inicia una nueva venta para comenzar a operar."
                minHeight="420px"
              />
            )}
          </section>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Global Modals */}
      {/* ===================================================== */}

      {/* 🔹 Multiple Payments */}
      <MultiplePaymentModal
        isOpen={showMultiplePaymentModal}
        onClose={closeMultiPaymentModal}
        customerName={form.customer?.name}
      />

      {/* 🔹 Add Product */}
      {selectedProductToAdd && (
        <POSAddProductToSaleModal
          isOpen={true}
          product={selectedProductToAdd}
          saleId={form?.id || 0}
          onClose={handleCloseProductModal}
          onConfirm={(payload) => {
            handleCloseProductModal();

            recipeGuard.guard(
              activeBranchId,
              payload.productId,
              payload.quantity,
              () => addProduct(payload)
            );
          }}
        />
      )}

      {/* 🔹 Aviso de materia prima (receta) */}
      <RecipeStockInfoModal
        availability={recipeGuard.pending?.availability ?? null}
        onConfirm={recipeGuard.confirm}
        onCancel={recipeGuard.cancel}
      />

      {/* 🔹 Update Selected Product */}
      {selectedProduct && (
        <POSUpdateSelectedProductModal
          isOpen={true}
          onClose={() => handlerSelectProduct(null)}
          onSave={(updated) => {
            updateProduct(updated);

            handlerSelectProduct(null);
          }}
        />
      )}
    </>
  );
};

export default POSSaleRegistrationPanel;
